/**
 * The reference lists the Add client form picks from — all of them, fetched
 * together on mount and reported one field at a time.
 *
 * The machinery is `useCatalogLookups`, which the Add enquiry form shares;
 * this is the client form's selection of it, and the names the rest of the
 * feature already refers to those lists by.
 */

import { useCatalogLookups } from "./useCatalogLookups";
import type { CatalogLookup, CatalogLookups } from "./useCatalogLookups";

export type { LookupStatus } from "./useCatalogLookups";

/** One list, as a field reads it. */
export type ClientLookup = CatalogLookup;

/**
 * Module-level and never rebuilt: the hook keys its fetch on these names, and
 * a fresh array per render would be a fresh set of requests per keystroke.
 *
 * `serviceGoals` rather than `goals` — the Goal field picks from the
 * practitioner's own active services, and what it sends is a service id. The
 * backend's goal catalog is still served, and the Add enquiry form still picks
 * from it; this form no longer asks for it.
 */
const CLIENT_CATALOGS = [
  "titles",
  "serviceGoals",
  "dietaryPatterns",
  "allergens",
  "cuisines",
  "lifeStyles",
  "medicalConditions",
] as const;

export type LookupName = (typeof CLIENT_CATALOGS)[number];

/** Every list the client form fetches, so a screen can report on them all. */
export const LOOKUP_NAMES: readonly LookupName[] = CLIENT_CATALOGS;

export type ClientLookups = CatalogLookups<LookupName>;

export function useClientLookups(): ClientLookups {
  return useCatalogLookups(CLIENT_CATALOGS);
}
